import { gql } from "graphql-request";
import { createGraphQLClient } from "./graphql.service";
import { FilterInput } from "./entity-instance.service";
import { ensureFirstLetterLowercase } from "@/lib/utils";

export interface PyloMedia {
  id: string;
  file_name: string;
  url: string;
  file_mime_type: string;
  file_size_bytes: number;
  created_at: string;
  updated_at: string;
  file_extension?: string;
  is_public?: boolean;
  integer_id?: number;
}

export interface PyloMediaListResponse {
  pyloMediaList: {
    data: PyloMedia[];
    pagination: {
      current_page: number;
      has_more_pages: boolean;
      total: number;
    };
  };
}

export interface GetPyloMediaListParams {
  filter?: FilterInput;
  pagination?: {
    page?: number;
    per_page?: number;
  };
}

const pyloMediaListQuery = gql`
  query media($filter: FilterInput, $pagination: PaginationInput) {
    pyloMediaList(filter: $filter, pagination: $pagination) {
      data {
        id
        file_name
        url
        file_mime_type
        file_size_bytes
        file_extension
        is_public
        integer_id
        created_at
        updated_at
      }
      pagination {
        current_page
        has_more_pages
        total
      }
    }
  }
`;

export const getPyloMediaList = async ({
  filter,
  pagination = { page: 1, per_page: 20 },
}: GetPyloMediaListParams = {}) => {
  const client = createGraphQLClient();

  try {
    const response = (await client.request(pyloMediaListQuery, {
      filter,
      pagination,
    })) as PyloMediaListResponse;

    if (!response.pyloMediaList) {
      throw new Error("Failed to fetch media files");
    }

    return response.pyloMediaList;
  } catch (error) {
    console.error("Error fetching pylo media list:", error);
    throw error;
  }
};

// Query to get a single PyloMedia by ID
const pyloMediaByIdQuery = gql`
  query pyloMediaById($id: ID!) {
    pyloMediaById(id: $id) {
      data {
        id
        file_name
        url
        file_mime_type
        file_size_bytes
        created_at
        updated_at
      }
    }
  }
`;

export const getPyloMediaById = async (id: string): Promise<PyloMedia> => {
  const client = createGraphQLClient();

  try {
    const response = (await client.request(pyloMediaByIdQuery, {
      id,
    })) as {
      pyloMediaById: {
        data: PyloMedia;
      };
    };

    if (!response.pyloMediaById?.data) {
      throw new Error(`Failed to fetch media file with ID: ${id}`);
    }

    return response.pyloMediaById.data;
  } catch (error) {
    console.error("Error fetching pylo media by ID:", error);
    throw error;
  }
};

/**
 * A relation the media library resolves usage through.
 *
 * `source: "inverse"` — an inverse field the backend generates on `PyloMedia`
 * (owner declared `target_field_name`). Filterable and queryable directly via
 * `PyloMedia.<fieldName>`.
 *
 * `source: "owner"` — a one-way relation whose owner named no inverse. No
 * field exists on `PyloMedia` for it, so usage is resolved by querying the
 * owner's list filtered by `<fieldName>.id = mediaId`; the media list can't
 * filter through it in a single tree either — see `useUsedByOwnerMediaIds`.
 */
export interface MediaUsageRelation {
  source: "inverse" | "owner";
  /**
   * Inverse: field on `PyloMedia`. Owner: field on `entityName` pointing to
   * `PyloMedia`.
   */
  fieldName: string;
  entityName: string;
  /** Field of the related entity to show as its label. */
  displayField: string;
  /** `*ListData` relations return an array, `*Data` a single record. */
  isList: boolean;
}

export interface MediaUsageEntry {
  relation: MediaUsageRelation;
  instanceId: string;
  label: string;
}

const usageSelection = (relations: Array<MediaUsageRelation>) =>
  relations
    .map(
      (relation) => `${relation.fieldName} {
        data {
          id
          ${relation.displayField === "id" ? "" : relation.displayField}
        }
      }`,
    )
    .join("\n");

const rowsToEntries = (
  relation: MediaUsageRelation,
  rows: Array<Record<string, unknown>>,
): Array<MediaUsageEntry> =>
  rows
    .filter((row): row is Record<string, unknown> => !!row && typeof row === "object")
    .map((row) => ({
      relation,
      instanceId: String(row.id),
      label: String(row[relation.displayField] ?? row.id ?? ""),
    }));

const fetchInverseUsage = async (
  id: string,
  relations: Array<MediaUsageRelation>,
): Promise<Array<MediaUsageEntry>> => {
  if (relations.length === 0) return [];
  const client = createGraphQLClient();
  const query = gql`
    query pyloMediaUsage($id: ID!) {
      pyloMediaById(id: $id) {
        data {
          id
          ${usageSelection(relations)}
        }
      }
    }
  `;

  const response = (await client.request(query, { id })) as {
    pyloMediaById: { data: Record<string, unknown> };
  };
  const media = response.pyloMediaById?.data ?? {};

  return relations.flatMap((relation) => {
    const envelope = media[relation.fieldName] as { data?: unknown } | undefined;
    const rows = envelope?.data;
    if (!rows) return [];
    return rowsToEntries(relation, Array.isArray(rows) ? rows : [rows]);
  });
};

const OWNER_LOOKUP_PAGE_SIZE = 200;

const fetchOwnerUsageOne = async (
  id: string,
  relation: MediaUsageRelation,
): Promise<Array<MediaUsageEntry>> => {
  const client = createGraphQLClient();
  const entity = ensureFirstLetterLowercase(relation.entityName);
  const listField = `${entity}List`;
  const selection = relation.displayField === "id" ? "id" : `id\n${relation.displayField}`;
  const query = gql`
    query mediaOwnerUsage($filter: FilterInput, $pagination: PaginationInput) {
      ${listField}(filter: $filter, pagination: $pagination) {
        data {
          ${selection}
        }
      }
    }
  `;
  const variables = {
    filter: {
      query: [{ condition: { field: `${relation.fieldName}.id`, operator: "equal", value: id } }],
    },
    pagination: { page: 1, per_page: OWNER_LOOKUP_PAGE_SIZE },
  };

  const response = (await client.request(query, variables)) as Record<
    string,
    { data: Array<Record<string, unknown>> }
  >;
  const rows = response[listField]?.data ?? [];
  return rowsToEntries(relation, rows);
};

/**
 * Fetch every entity instance referencing this file, across all relations.
 * Inverse-source relations are resolved in one query via `PyloMedia`; owner-
 * source relations fan out one query per owning entity because no GraphQL
 * field exists on `PyloMedia` to traverse them.
 */
export const getPyloMediaUsage = async (
  id: string,
  relations: Array<MediaUsageRelation>,
): Promise<Array<MediaUsageEntry>> => {
  if (relations.length === 0) return [];

  const inverse = relations.filter((relation) => relation.source === "inverse");
  const owner = relations.filter((relation) => relation.source === "owner");

  const [inverseEntries, ...ownerBatches] = await Promise.all([
    fetchInverseUsage(id, inverse),
    ...owner.map((relation) => fetchOwnerUsageOne(id, relation)),
  ]);

  return [...inverseEntries, ...ownerBatches.flat()];
};

/**
 * Media IDs referenced through an owner-source relation. Used to translate an
 * owner-side "Used by" selection into an `id IN (…)` filter on `PyloMedia`.
 * With `instanceId`, only that owner's connections; without it, the union
 * across every owner of that entity — bounded by `OWNER_LOOKUP_PAGE_SIZE`
 * because a single-page probe is enough to answer "is any owner referencing
 * media X" for the common facet case.
 */
export const getMediaIdsReferencedByOwnerRelation = async (
  relation: MediaUsageRelation,
  instanceId?: string,
): Promise<Array<string>> => {
  const client = createGraphQLClient();
  const entity = ensureFirstLetterLowercase(relation.entityName);

  if (instanceId) {
    const byIdField = `${entity}ById`;
    const query = gql`
      query mediaIdsForOwnerInstance($id: ID!) {
        ${byIdField}(id: $id) {
          data {
            id
            ${relation.fieldName} {
              data { id }
            }
          }
        }
      }
    `;
    const response = (await client.request(query, { id: instanceId })) as Record<
      string,
      { data: Record<string, unknown> | null }
    >;
    const owner = response[byIdField]?.data;
    if (!owner) return [];
    const envelope = owner[relation.fieldName] as { data?: unknown } | undefined;
    const rows = envelope?.data;
    if (!rows) return [];
    const list = Array.isArray(rows) ? rows : [rows];
    return list
      .filter((row): row is Record<string, unknown> => !!row && typeof row === "object")
      .map((row) => String(row.id));
  }

  const listField = `${entity}List`;
  const query = gql`
    query mediaIdsForOwners($pagination: PaginationInput) {
      ${listField}(pagination: $pagination) {
        data {
          id
          ${relation.fieldName} {
            data { id }
          }
        }
      }
    }
  `;
  const response = (await client.request(query, {
    pagination: { page: 1, per_page: OWNER_LOOKUP_PAGE_SIZE },
  })) as Record<string, { data: Array<Record<string, unknown>> }>;
  const owners = response[listField]?.data ?? [];

  const ids = new Set<string>();
  for (const owner of owners) {
    const envelope = owner[relation.fieldName] as { data?: unknown } | undefined;
    const rows = envelope?.data;
    if (!rows) continue;
    const list = Array.isArray(rows) ? rows : [rows];
    for (const row of list) {
      if (row && typeof row === "object") ids.add(String((row as { id: unknown }).id));
    }
  }
  return Array.from(ids);
};

const deletePyloMediaMutation = gql`
  mutation deletePyloMedia($ids: [ID!]!) {
    deletePyloMedia(ids: $ids) {
      data {
        success
      }
    }
  }
`;

export const deletePyloMedia = async (ids: Array<string>): Promise<boolean> => {
  const client = createGraphQLClient();
  const response = (await client.request(deletePyloMediaMutation, { ids })) as {
    deletePyloMedia: { data: { success: boolean } };
  };
  return response.deletePyloMedia?.data?.success ?? false;
};

// Helper function to create mime type filter
export const createMimeTypeFilter = (mimeTypes: string[]): FilterInput => {
  if (mimeTypes.length === 0) {
    return {};
  }

  return {
    query: [
      {
        condition: {
          field: "file_mime_type",
          operator: "in",
          values: mimeTypes,
        },
      },
    ],
  };
};

// Common mime type filters
export const IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
];

export const DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
];

export const VIDEO_MIME_TYPES = ["video/mp4", "video/webm", "video/ogg", "video/quicktime"];

export const AUDIO_MIME_TYPES = ["audio/mpeg", "audio/wav", "audio/ogg", "audio/mp4"];

// Helper function to get filter for specific file types
export const getFileTypeFilter = (
  fileType: "images" | "documents" | "videos" | "audio" | "all",
): FilterInput => {
  switch (fileType) {
    case "images":
      return createMimeTypeFilter(IMAGE_MIME_TYPES);
    case "documents":
      return createMimeTypeFilter(DOCUMENT_MIME_TYPES);
    case "videos":
      return createMimeTypeFilter(VIDEO_MIME_TYPES);
    case "audio":
      return createMimeTypeFilter(AUDIO_MIME_TYPES);
    case "all":
    default:
      return {};
  }
};
